import { Net, } from "@buildwithflux/models";
import { keySort } from "@buildwithflux/shared";
import { addPinsToNet } from "../document/models";
import { devAssert } from "../helpers";
import { filterTruthy } from "../helpers/arrays";
import { deleteNetsExcludingDescendants } from "./deleteNets";
export function mergeNets(document, merged, oldNetsHashMap, pcbNodes, netHashLookup, allNewNets, logger) {
    const selectionUpdates = [];
    for (const [newNetHash, oldNetHashes] of Object.entries(merged)) {
        const newNet = allNewNets[newNetHash];
        // We respect complexity order when merging nets, except in the case that one of the nets is
        // ground, in which case we merge the other net into the ground net.
        // TODO FLUX-5090: these are already sorted by num pins (complexity) when generated.
        // Why not also do this sort at the same time?
        const [mergeWinner, ...mergeLosers] = oldNetHashes.sort(keySort((uid) => Net.terminalsIncludeGround(Object.keys(oldNetsHashMap[uid]), document.elements) ? 0 : 1));
        if (!mergeWinner)
            throw new Error("No merge winner");
        const mergeWinnerId = netHashLookup[mergeWinner];
        if (!mergeWinnerId)
            throw new Error("No merge winner ID");
        const winnerNet = document.nets[mergeWinnerId];
        if (!winnerNet)
            throw new Error("No merge winner net found in document");
        // Add this net to the selection updates
        selectionUpdates.push(mergeWinnerId);
        const terminals = newNet.terminalsByDocLevel();
        // Add pins from merge losers to merge winner, if they don't already exist
        // TODO FLUX-5090 we should not just add new pins here; we should ensure all pins are as expected.
        // It's too risky the way it is -- although for right now I don't think we have a concrete
        // bug that this could cause (merged nets only ever add new pins), in future that easily
        // may not be the case.
        addPinsToNet(document.nets, mergeWinnerId, terminals.topLevel);
        // Build a list of props already on the merge winner net so losers can't override them
        const definedProps = new Set(Object.values(winnerNet.properties).map((prop) => prop.name));
        // Add children from merge losers to merge winner
        const mergeWinnerNode = pcbNodes[mergeWinnerId];
        // TODO it would be much better to throw a hard error here, because continuing in this condition would
        // normally mean perpetuating data corruption. But I don't want to cause legacy documents to fail.
        // So we rely on the fact that we know (for now) that the sole caller, updateNetNodes, retroactively
        // ensures a pcb node exists for every net.
        // The near-term plan is to do a minor refactor on this code to have better provability up front.
        devAssert(mergeWinnerNode, "Merge winner PCB node not found", logger);
        if (mergeWinnerNode) {
            // Update the merged net terminals
            mergeWinnerNode.terminals = Net.terminalsToMap(terminals.topLevel);
            mergeWinnerNode.submoduleTerminals = Net.terminalsToMap(terminals.submodule);
            const isGround = Net.terminalsIncludeGround(terminals.topLevel, document.elements);
            if (isGround) {
                // Ensure the net name gets updated to ground
                winnerNet.label = "GND";
                mergeWinnerNode.name = "GND";
            }
        }
        for (const mergeLoser of mergeLosers) {
            const mergeLoserNode = pcbNodes[netHashLookup[mergeLoser]];
            // TODO similarly to mergeWinnerNode, we should really be failing hard here -- document corruption.
            // But we work around that in the sole caller for now. See comment above.
            devAssert(mergeLoserNode, "Merge loser PCB node not found", logger);
            if (mergeLoserNode) {
                for (const [key, rule] of Object.entries(mergeLoserNode.pcbNodeRuleSet)) {
                    // TODO see above about [not] failing hard when mergeWinnerNode doesn't exist.
                    if (mergeWinnerNode && !mergeWinnerNode.pcbNodeRuleSet[key]) {
                        mergeWinnerNode.pcbNodeRuleSet[key] = rule;
                    }
                }
            }
            const mergeLoserNet = document.nets[netHashLookup[mergeLoser]];
            if (!mergeLoserNet)
                throw new Error(`Merging net that does not exist in document: ${mergeLoser}`);
            // Add properties from merge losers to merge winner, if they don't already exist
            for (const [key, prop] of Object.entries(mergeLoserNet.properties)) {
                if (!definedProps.has(prop.name)) {
                    winnerNet.properties[key] = prop;
                }
            }
        }
        // Remove merge losers
        // TODO FLUX-5090 I'm *fairly* sure now that we can just delete the whole subtree for deleted nets (which would
        // be safer for document consistency). See comment above deleteNetsExcludingDescendants callsite
        // in PcbTreeManager.
        deleteNetsExcludingDescendants(document, filterTruthy(mergeLosers.map((loser) => netHashLookup[loser])), pcbNodes);
        // Update the hash of the merge winner
        winnerNet.hash = newNetHash;
    }
    return selectionUpdates;
}
//# sourceMappingURL=mergeNets.js.map